import React from 'react';
import SelectBase, {components as defaultComponents, ControlProps} from 'react-select';
import type {
	GroupBase,
	Props as ReactSelectProps,
	StylesConfig,
	Theme,
	ThemeConfig,
} from 'react-select';
import {useTranslation} from 'react-i18next';
import {useFormikContext, FormikContextType} from 'formik';
import cls from './CustomSelect.module.css';

export type SelectSize = 'sm' | 'md' | 'lg';

export type SelectColorConfig = {
	background?: string;
	backgroundHover?: string;
	backgroundFocused?: string;
	border?: string;
	borderHover?: string;
	borderFocused?: string;
	text?: string;
	placeholder?: string;
	inputText?: string;
	optionText?: string;
	optionHoverText?: string;
	optionSelectedText?: string;
	optionBackground?: string;
	optionHoverBackground?: string;
	optionSelectedBackground?: string;
	indicator?: string;
	indicatorHover?: string;
	multiTagBackground?: string;
	multiTagText?: string;
	disabledBackground?: string;
	disabledText?: string;
	menuBackground?: string;
	menuBorder?: string;
	scrollbarThumb?: string;
	scrollbarTrack?: string;
};

export type SelectDimensionConfig = {
	controlHeight?: number; // px
	fontSize?: number; // px
	borderRadius?: number; // px
	horizontalPadding?: number; // px inside control
	optionHeight?: number; // px
	menuElevation?: number; // box-shadow elevation factor (0..1)
};

export type BaseOption = {label: string; value: string | number};

export type FlexibleSelectProps<
	Option extends BaseOption = BaseOption,
	IsMulti extends boolean = false,
	Group extends GroupBase<Option> = GroupBase<Option>,
> = {
	size?: SelectSize;
	colors?: SelectColorConfig;
	dimensions?: SelectDimensionConfig;
	className?: string;
	hideIndicators?: boolean;
	isBare?: boolean; // if true, remove borders/backgrounds; rely on container
	isDisabled?: boolean;
	icon?: React.ReactNode; // optional icon to display in control
	name?: string; // optional formik field name for auto errors
	showError?: boolean;
	error?: string; // explicit error override
	// Allow consumers to extend/override styles and theme
	overrideStyles?: StylesConfig<Option, IsMulti, Group>;
	overrideTheme?: (theme: Theme) => Theme;
} & Omit<
	ReactSelectProps<Option, IsMulti, Group>,
	'styles' | 'theme' | 'className' | 'isDisabled'
>;

const sizeToDimensions: Record<SelectSize, Required<SelectDimensionConfig>> = {
	sm: {
		controlHeight: 32,
		fontSize: 12,
		borderRadius: 6,
		horizontalPadding: 10,
		optionHeight: 32,
		menuElevation: 0.15,
	},
	md: {
		controlHeight: 40,
		fontSize: 14,
		borderRadius: 8,
		horizontalPadding: 12,
		optionHeight: 38,
		menuElevation: 0.18,
	},
	lg: {
		controlHeight: 48,
		fontSize: 14,
		borderRadius: 8,
		horizontalPadding: 12,
		optionHeight: 44,
		menuElevation: 0.22,
	},
};

const defaultColors: Required<SelectColorConfig> = {
	background: '#ffffff',
	backgroundHover: '#ffffff',
	backgroundFocused: '#ffffff',
	border: '#D0D5DD',
	borderHover: '#98A2B3',
	borderFocused: '#7a934f',
	text: '#101828',
	placeholder: '#98A2B3',
	inputText: '#101828',
	optionText: '#101828',
	optionHoverText: '#101828',
	optionSelectedText: '#101828',
	optionBackground: 'transparent',
	optionHoverBackground: '#F2F4F7',
	optionSelectedBackground: '#EEF2FF',
	indicator: '#667085',
	indicatorHover: '#344054',
	multiTagBackground: '#EEF2FF',
	multiTagText: '#344054',
	disabledBackground: '#F9FAFB',
	disabledText: '#D0D5DD',
	menuBackground: '#ffffff',
	menuBorder: '#EAECF0',
	scrollbarThumb: '#D0D5DD',
	scrollbarTrack: 'transparent',
};

function mergeDimensions(size: SelectSize, custom?: SelectDimensionConfig) {
	const base = sizeToDimensions[size];
	return {...base, ...custom} as Required<SelectDimensionConfig>;
}

function createResetStyles<
	Option extends BaseOption,
	IsMulti extends boolean,
	Group extends GroupBase<Option>,
>(
	size: SelectSize,
	dims: Required<SelectDimensionConfig>,
	colors: Required<SelectColorConfig>,
	hideIndicators: boolean,
	isBare: boolean,
): StylesConfig<Option, IsMulti, Group> {
	const controlPaddingRight = hideIndicators
		? dims.horizontalPadding
		: dims.horizontalPadding;

	return {
		// Reset container spacing; leave layout to parent
		container: (base) => ({
			...base,
			margin: 0,
			padding: 0,
		}),
		control: (base, state) => ({
			// Reset all UA/RS defaults
			boxSizing: 'border-box',
			display: 'flex',
			alignItems: 'center',
			width: base.width,
			minHeight: dims.controlHeight,
			height: dims.controlHeight,
			backgroundColor: state.isDisabled
				? colors.disabledBackground
				: state.isFocused
					? colors.backgroundFocused
					: colors.background,
			color: colors.text,
			borderRadius: dims.borderRadius,
			borderWidth: isBare ? 0 : 1,
			borderStyle: isBare ? 'none' : 'solid',
			borderColor: state.isDisabled
				? colors.disabledText
				: state.isFocused
					? colors.borderFocused
					: state.menuIsOpen
						? colors.borderHover
						: colors.border,
			boxShadow: 'none',
			outline: 'none',
			transition: 'border-color 120ms ease, background-color 120ms ease',
			cursor: state.isDisabled ? 'not-allowed' : base.cursor,
			paddingLeft: dims.horizontalPadding,
			paddingRight: controlPaddingRight,
			// Remove blue focus ring in some browsers
			['&:focus']: {outline: 'none'},
			['&:hover']: {
				borderColor: state.isFocused
					? colors.borderFocused
					: colors.borderHover,
				backgroundColor: state.isFocused
					? colors.backgroundFocused
					: colors.backgroundHover,
			},
		}),
		valueContainer: (base) => ({
			...base,
			padding: 0,
			gap: 8,
		}),
		input: (base) => ({
			...base,
			margin: 0,
			padding: 0,
			color: colors.inputText,
			fontSize: dims.fontSize,
		}),
		placeholder: (base) => ({
			...base,
			margin: 0,
			color: colors.placeholder,
			fontSize: dims.fontSize,
		}),
		singleValue: (base) => ({
			...base,
			margin: 0,
			color: colors.text,
			fontSize: dims.fontSize,
		}),
		multiValue: (base) => ({
			...base,
			backgroundColor: colors.multiTagBackground,
			borderRadius: Math.max(4, Math.round(dims.borderRadius * 0.6)),
		}),
		multiValueLabel: (base) => ({
			...base,
			color: colors.multiTagText,
			fontSize: Math.max(11, Math.round(dims.fontSize - 1)),
			paddingRight: 2,
		}),
		multiValueRemove: (base) => ({
			...base,
			color: colors.multiTagText,
			['&:hover']: {
				backgroundColor: 'transparent',
				color: colors.indicatorHover,
			},
		}),
		indicatorsContainer: (base) => ({
			...base,
			display: hideIndicators ? 'none' : base.display,
		}),
		indicatorSeparator: () => ({display: 'none'}),
		dropdownIndicator: (base, state) => ({
			...base,
			color: state.isFocused ? colors.indicatorHover : colors.indicator,
			padding: 6,
			['&:hover']: {color: colors.indicatorHover},
		}),
		clearIndicator: (base) => ({
			...base,
			color: colors.indicator,
			padding: 6,
			['&:hover']: {color: colors.indicatorHover},
		}),
		menu: (base) => ({
			...base,
			backgroundColor: colors.menuBackground,
			margin: 4,
			borderRadius: Math.max(6, dims.borderRadius),
			border: `1px solid ${colors.menuBorder}`,
			boxShadow: `0 8px 20px rgba(16, 24, 40, ${dims.menuElevation})`,
			overflow: 'hidden',
		}),
		menuList: (base) => ({
			...base,
			padding: 0,
			maxHeight: 280,
			// custom scrollbar
			['&::-webkit-scrollbar']: {width: 8, height: 8},
			['&::-webkit-scrollbar-thumb']: {
				backgroundColor: colors.scrollbarThumb,
				borderRadius: 8,
			},
			['&::-webkit-scrollbar-track']: {backgroundColor: colors.scrollbarTrack},
		}),
		option: (base, state) => ({
			...base,
			backgroundColor: state.isSelected
				? colors.optionSelectedBackground
				: state.isFocused
					? colors.optionHoverBackground
					: colors.optionBackground,
			color: state.isSelected
				? colors.optionSelectedText
				: state.isFocused
					? colors.optionHoverText
					: colors.optionText,
			cursor: state.isDisabled ? 'not-allowed' : 'pointer',
			minHeight: dims.optionHeight,
			padding: '8px 12px',
			['&:active']: {backgroundColor: colors.optionHoverBackground},
		}),
		noOptionsMessage: (base) => ({
			...base,
			color: colors.placeholder,
			fontSize: dims.fontSize,
		}),
		loadingMessage: (base) => ({
			...base,
			color: colors.placeholder,
			fontSize: dims.fontSize,
		}),
	};
}

function resetTheme(theme: Theme): Theme {
	// Neutralize react-select theme to avoid blue highlights
	return {
		...theme,
		colors: {
			...theme.colors,
			primary: 'transparent',
			primary25: 'transparent',
			primary50: 'transparent',
			primary75: 'transparent',
			danger: theme.colors.danger,
			dangerLight: theme.colors.dangerLight,
			neutral0: 'transparent',
			neutral5: 'transparent',
			neutral10: 'transparent',
			neutral20: 'transparent',
			neutral30: 'transparent',
			neutral40: 'inherit',
			neutral50: 'inherit',
			neutral60: 'inherit',
			neutral70: 'inherit',
			neutral80: 'inherit',
			neutral90: 'inherit',
		},
		spacing: {baseUnit: 4, controlHeight: 0, menuGutter: 4},
		borderRadius: 0,
	};
}

export function CustomSelect<
	Option extends BaseOption = BaseOption,
	IsMulti extends boolean = false,
	Group extends GroupBase<Option> = GroupBase<Option>,
>(props: FlexibleSelectProps<Option, IsMulti, Group>) {
	const {t} = useTranslation();
	const {
        size = 'lg',
		colors: colorsProp,
		dimensions: dimsProp,
		className,
		hideIndicators = false,
		isBare = false,
		isDisabled,
		icon,
		name,
		showError = true,
		error: errorProp,
		overrideStyles,
		overrideTheme,
		components,
		...rest
	} = props as FlexibleSelectProps<Option, IsMulti, Group> & {
		components?: ReactSelectProps<Option, IsMulti, Group>['components'];
	};

	const colors = {
		...defaultColors,
		...(colorsProp || {}),
	} as Required<SelectColorConfig>;
	const dims = mergeDimensions(size, dimsProp);

	const styles = React.useMemo<StylesConfig<Option, IsMulti, Group>>(
		() => ({
			...createResetStyles<Option, IsMulti, Group>(
				size,
				dims,
				colors,
				hideIndicators,
				isBare,
			),
			...(overrideStyles || {}),
		}),
		[size, dims, colors, hideIndicators, isBare, overrideStyles],
	);

	const themeFn: (theme: Theme) => Theme = React.useCallback(
		(theme: Theme) =>
			overrideTheme ? overrideTheme(resetTheme(theme)) : resetTheme(theme),
		[overrideTheme],
	);

	function useMaybeFormik(n?: string): FormikContextType<any> | null {
		try {
			const ctx = useFormikContext<any>();
			return n ? ctx : null;
		} catch {
			return null;
		}
	}

	const formik = useMaybeFormik(name);
	let errorText: string | undefined = errorProp;
	if (formik && name) {
		const {status, errors, touched} = formik;
		if (errorText == null) {
			if (status && (status as any)[name]) errorText = (status as any)[name];
			else if (touched && (touched as any)[name] && errors && (errors as any)[name]) errorText = (errors as any)[name];
		}
	}

	// Create custom Control component with icon if provided
	const customComponents = React.useMemo(() => {
		if (icon) {
			const ControlWithIcon = ({
				children,
				...controlProps
			}: ControlProps<Option, IsMulti, Group>) => {
				return (
					<defaultComponents.Control {...controlProps}>
						<div style={{display: 'flex', alignItems: 'center', marginRight: '8px'}}>
							{icon}
						</div>
						{children}
					</defaultComponents.Control>
				);
			};
			return {
				...components,
				Control: ControlWithIcon,
			};
		}
		return components || defaultComponents;
	}, [icon, components]);

	return (
		<div className={`${cls.wrap} ${className || ''}`.trim()}>
			<SelectBase
				className={className}
				isDisabled={isDisabled}
				styles={styles}
				noOptionsMessage={() => t('Немає результатів')}
				theme={themeFn}
				components={customComponents}
				{...(rest as ReactSelectProps<Option, IsMulti, Group>)}
			/>
			{showError && errorText ? <div className={cls.error}>{errorText}</div> : null}
		</div>
	);
}

export default CustomSelect;
